import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Box, Stack } from '@mui/material';
import { ROUTES } from 'config';
import {
  Button,
  ListTotal,
  NumberInput,
  Panel,
  SwitchInput,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import {
  COLOR_BY_PERIOD_STATUS,
  DEFAULT_EARNING_FEE_BPS,
  MAX_EARNING_FEE_PERCENT,
  earningFeeBpsToPercent,
  earningFeePercentToBps,
  useAdminCloseEarningPeriod,
  useAdminEarningPeriods,
  useAdminOpenEarningPeriod,
  useAdminSupplier,
  useAdminUpdateSupplier,
} from 'store/admin-suppliers';
import { useConfirm } from 'store/confirm';
import { usePredefinedToasts } from 'utils/Toast';
import { formatPercents } from 'utils/format';
import { useRouteParams } from 'utils/useIdParam';

interface Values {
  earningFeePercent: number;
}

const parseFeePercent = (raw: string) => {
  if (!raw || raw === '-') {
    return NaN;
  }

  return Number(raw.replace(',', '.'));
};

interface EarningFeeFormProps {
  savedPercent: number;
  latestFeeBps: number;
  isUpdating: boolean;
  onSave: (percent: number) => void;
}

const EarningFeeForm = ({
  savedPercent,
  latestFeeBps,
  isUpdating,
  onSave,
}: EarningFeeFormProps) => {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<Values>({
    defaultValues: { earningFeePercent: savedPercent },
  });
  const [draftFee, setDraftFee] = useState(String(savedPercent));

  const draftPercent = parseFeePercent(draftFee);
  const feeChanged =
    !Number.isNaN(draftPercent) &&
    earningFeePercentToBps(
      Math.min(MAX_EARNING_FEE_PERCENT, Math.max(0, draftPercent)),
    ) !== latestFeeBps;

  const onSubmit: SubmitHandler<Values> = () => {
    const percent = Math.min(
      MAX_EARNING_FEE_PERCENT,
      Math.max(0, draftPercent),
    );

    if (Number.isNaN(percent) || !feeChanged) {
      return;
    }

    onSave(percent);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack
        direction="row"
        alignItems="stretch"
        gap={1}
        sx={{ maxWidth: 360 }}
      >
        <Controller
          name="earningFeePercent"
          control={control}
          render={({ field: { name, value, onChange, ref } }) => (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <NumberInput
                label={t('marketplaceSuppliers.melpFee', {
                  defaultValue: 'MELP fee',
                })}
                name={name}
                value={value}
                onChange={onChange}
                onInputChange={setDraftFee}
                key={value}
                InputProps={{
                  endAdornment: '%',
                }}
                ref={ref}
              />
            </Box>
          )}
        />

        <Box
          sx={{
            display: 'flex',
            '& > button': {
              height: '100%',
              minHeight: '100%',
              borderRadius: '6px',
            },
          }}
        >
          <Button
            label={t('common.update', { defaultValue: 'Update' })}
            type="submit"
            disabled={!feeChanged || isUpdating}
            loading={isUpdating}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export const AdminSupplierTracking = () => {
  const { t, i18n } = useTranslation();
  const { id: supplierId } = useRouteParams(ROUTES.admin.suppliers.tracking);
  const { confirmation } = useConfirm();
  const predefinedToasts = usePredefinedToasts();

  const { data: supplier } = useAdminSupplier(supplierId);
  const { data: periods, isLoading: isLoadingPeriods } =
    useAdminEarningPeriods(supplierId);

  const { mutate: openPeriod, isLoading: isOpening } =
    useAdminOpenEarningPeriod();
  const { mutate: closePeriod, isLoading: isClosing } =
    useAdminCloseEarningPeriod();
  const { mutate: updateSupplier, isLoading: isUpdatingFee } =
    useAdminUpdateSupplier();

  const latestFeeBps =
    periods?.find((period) => period.isOpen)?.feeBps ??
    periods?.[0]?.feeBps ??
    supplier?.earningFeeBps ??
    DEFAULT_EARNING_FEE_BPS;
  const savedPercent = earningFeeBpsToPercent(latestFeeBps);
  const feeReady = periods !== undefined;

  const trackingEnabled = Boolean(periods?.some((period) => period.isOpen));
  const isToggling = isOpening || isClosing;

  const handleToggle = async (enabled: boolean) => {
    if (enabled) {
      openPeriod({ supplierId });
      return;
    }

    const { confirmed } = await confirmation(
      t('marketplaceSuppliers.disableTrackingConfirmation', {
        defaultValue:
          'Stop tracking earnings for this supplier? Completed sales will no longer generate earnings until tracking is enabled again.',
      }),
    );

    if (confirmed) {
      closePeriod({ supplierId });
    }
  };

  return (
    <Stack gap={2}>
      <Panel
        title={t('marketplaceSuppliers.tracking', { defaultValue: 'Tracking' })}
      >
        <Stack gap={2}>
          <SwitchInput
            label={t('marketplaceSuppliers.trackingEnabled', {
              defaultValue: 'Tracking enabled',
            })}
            value={trackingEnabled}
            onChange={handleToggle}
            disabled={isToggling || isLoadingPeriods}
          />

          {feeReady && (
            <EarningFeeForm
              key={savedPercent}
              savedPercent={savedPercent}
              latestFeeBps={latestFeeBps}
              isUpdating={isUpdatingFee}
              onSave={(percent) => {
                updateSupplier(
                  {
                    supplierId,
                    data: { earningFeeBps: earningFeePercentToBps(percent) },
                  },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                    },
                  },
                );
              }}
            />
          )}
        </Stack>
      </Panel>

      <TableWrapper>
        <TableToolbar>
          <ListTotal total={periods?.length} />
        </TableToolbar>
        <Table
          data={periods ?? []}
          isLoading={isLoadingPeriods}
          columns={[
            {
              id: 'status',
              header: t('common.status'),
              cell: ({ row: { original: period } }) => {
                const status = period.isOpen ? 'open' : 'closed';

                return (
                  <Typography color={COLOR_BY_PERIOD_STATUS[status]}>
                    {period.isOpen
                      ? t('marketplaceSuppliers.periodOpen', {
                          defaultValue: 'Open',
                        })
                      : t('marketplaceSuppliers.periodClosed', {
                          defaultValue: 'Closed',
                        })}
                  </Typography>
                );
              },
            },
            {
              id: 'feeBps',
              header: t('marketplaceSuppliers.melpFee', {
                defaultValue: 'MELP fee',
              }),
              cell: ({ row: { original: period } }) =>
                formatPercents(i18n.language, period.feeBps / 10_000),
            },
            {
              id: 'startsAt',
              header: t('marketplaceSuppliers.startsAt', {
                defaultValue: 'Starts at',
              }),
              cell: ({ row: { original: period } }) =>
                moment(period.startsAt).format('l LT'),
            },
            {
              id: 'endsAt',
              header: t('marketplaceSuppliers.endsAt', {
                defaultValue: 'Ends at',
              }),
              cell: ({ row: { original: period } }) =>
                period.endsAt ? moment(period.endsAt).format('l LT') : '—',
            },
          ]}
        />
      </TableWrapper>
    </Stack>
  );
};
